import {
  Box,
  Card,
  CardContent,
  Typography
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { User } from 'services/admin-users';

interface RevisionsByUserChartProps {
  members: User[];
}

const RevisionsByUserChart: React.FC<RevisionsByUserChartProps> = ({ members }) => {
  const { t } = useTranslation();
  // Prepare revisions by user data from members
  const prepareRevisionsByUser = () => {
    // Filter members with revisions and sort by revision count
    return members
      .filter(member => (member as any).revisionCount > 0)
      .map(member => ({
        userName: member.name,
        count: (member as any).revisionCount || 0,
        displayName: member.name.length > 15 ? member.name.substring(0, 15) + '...' : member.name
      }))
      .sort((a, b) => b.count - a.count) // Sort by count descending
      .slice(0, 10); // Top 10 users
  };

  const userData = prepareRevisionsByUser();

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;
      return (
        <Box sx={{ 
          backgroundColor: 'white', 
          border: '1px solid #ccc', 
          p: 1, 
          borderRadius: 1,
          boxShadow: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {dataPoint?.userName || label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
            Total Revisions: {dataPoint?.count || payload[0]?.value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{ height: 400 }}>
      <CardContent sx={{ height: '100%', p: 2 }}>
        <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 'bold', mb: 2 }}>
            {t('ADMIN_STATS_TOP_USERS_BY_REVISIONS')}
        </Typography>
        
        {userData.length > 0 ? (
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={userData} margin={{ left: 10, right: 20, top: 5, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="displayName" 
                tick={{ fontSize: 10 }}
                angle={0}
                textAnchor="middle"
                height={80}
                interval={0}
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                width={40}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="count" 
                fill="#9c27b0" 
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '90%',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              No user revision data available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RevisionsByUserChart; 